import React, { useCallback } from 'react';
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Printer, Download, RotateCcw, Settings, Pencil } from 'lucide-react';
import { PageSettings } from '../../hooks/usePdfExportStateV2';

interface PreviewToolbarProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  pageSettings: PageSettings;
  onPageSettingsChange: (settings: Partial<PageSettings>) => void;
  onPrint: () => void;
  onReset: () => void;
  pageDimensions: { width: number; height: number };
  marginPixels: number;
  isEditMode?: boolean;
  onEditModeChange?: (enabled: boolean) => void;
  /** True while the two-phase renderer is still measuring + paginating.
   *  Used to disable the Print button so users can't fire window.print()
   *  while pageAssignments only contains the initial 1-page placeholder. */
  isPaginating?: boolean;
}

export const PreviewToolbar: React.FC<PreviewToolbarProps> = ({
  zoom,
  onZoomChange,
  currentPage,
  totalPages,
  onPageChange,
  pageSettings,
  onPageSettingsChange,
  onPrint,
  onReset,
  pageDimensions,
  marginPixels,
  isEditMode = false,
  onEditModeChange,
  isPaginating = false
}) => {
  const [showSettings, setShowSettings] = React.useState(false);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    onZoomChange(Math.min(zoom + 0.1, 1.5));
  }, [zoom, onZoomChange]);

  const handleZoomOut = useCallback(() => {
    onZoomChange(Math.max(zoom - 0.1, 0.25));
  }, [zoom, onZoomChange]);

  // Page navigation
  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, onPageChange]);

  return (
    <>
      <div className="preview-toolbar flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200 sticky top-0 z-30">
        {/* Zoom controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4 text-gray-600" />
          </button>
          <span className="text-sm text-gray-700 min-w-[60px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Page indicator */}
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {/* Page info */}
          <span className="text-xs text-gray-400">
            {pageSettings.size.toUpperCase()} • {pageSettings.orientation} • {pageSettings.margins}mm margins
          </span>

          {/* Edit mode toggle */}
          {onEditModeChange && (
            <button
              onClick={() => onEditModeChange(!isEditMode)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors ${
                isEditMode
                  ? 'bg-amber-100 text-amber-700 border border-amber-300'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
              title={isEditMode ? "Exit edit mode" : "Edit content"}
            >
              <Pencil className="w-4 h-4" />
              <span className="text-sm font-medium">{isEditMode ? 'Editing' : 'Edit'}</span>
            </button>
          )}

          {/* Settings button */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`p-1.5 rounded transition-colors ${
              showSettings ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-200 text-gray-600'
            }`}
            title="Page settings"
          >
            <Settings className="w-4 h-4" />
          </button>

          {/* Reset button */}
          <button
            onClick={onReset}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            title="Reset to defaults"
          >
            <RotateCcw className="w-4 h-4 text-gray-600" />
          </button>

          {/* Print button — disabled until pagination completes so users
              can't print just the initial 1-page placeholder. */}
          <button
            onClick={onPrint}
            disabled={isPaginating}
            className="flex items-center gap-2 px-4 py-1.5 bg-[#1993e5] text-white rounded hover:bg-[#1580c9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={isPaginating ? 'Preparing pages…' : 'Print / Export'}
          >
            <Printer className="w-4 h-4" />
            <span className="text-sm font-medium">{isPaginating ? 'Preparing…' : 'Print / Export'}</span>
          </button>
        </div>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="settings-panel bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-6">
          {/* Page size */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Size:</label>
            <select
              value={pageSettings.size}
              onChange={(e) => onPageSettingsChange({ size: e.target.value as 'a4' | 'letter' })}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="a4">A4</option>
              <option value="letter">Letter</option>
            </select>
          </div>

          {/* Orientation */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Orientation:</label>
            <select
              value={pageSettings.orientation}
              onChange={(e) => onPageSettingsChange({ orientation: e.target.value as 'portrait' | 'landscape' })}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>

          {/* Margins */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Margins:</label>
            <select
              value={pageSettings.margins}
              onChange={(e) => onPageSettingsChange({ margins: parseInt(e.target.value) })}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10mm</option>
              <option value={15}>15mm</option>
              <option value={20}>20mm</option>
              <option value={25}>25mm</option>
            </select>
          </div>

          {/* Dimensions info */}
          <div className="ml-auto text-xs text-gray-400">
            {pageDimensions.width}×{pageDimensions.height}px @ 96 DPI
          </div>
        </div>
      )}
    </>
  );
};

export default PreviewToolbar;
